import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    ERequestStatus,
    requestByConnection,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { ISubmitParams } from '~/src/components/RequestFormPhone';
import { dataLayerForCBPhonePush, divideUsersGA } from '~/src/helpers/eventsForDataLayer';
import { IDataLayerPushUid } from '~/src/helpers/eventsForDataLayer';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

export type { ISubmitParams };

enum EMediaWidth {
    mobile = '(max-width: 1365px)',
}

export interface ICallBackFormRoutersProps {
    isAuth: boolean,
    agreement?: number,
}

const CallBackFormRouters = ({ isAuth, agreement }: ICallBackFormRoutersProps): JSX.Element => {
    const isMobile = useMatchMedia(EMediaWidth.mobile);
    const csrf = useSelector(selectCsrf);

    const sendGTMevent = useCallback((ok: boolean, labelText = '', message?: string) => {
        dataLayerForCBPhonePush({
            event: 'UAevent',
            category: CATEGORIES.applicationForm,
            action: divideUsersGA(ok, isAuth),
            label: `${labelText} | ${message}`,
            uid: isAuth ? agreement : null,
        } as unknown as IDataLayerPushUid);
    }, []);

    const submitRequest = useCallback(async(params: ISubmitParams, labelText = '') => {
        const dataParams = {
            fio: 'Потенциальный клиент с сайта',
            phone: params.phone,
            product_id: [EProductsId.internet],
            check_call_type: window && window.location.href,
            csrf,
        };

        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection(dataParams),
        );

        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            sendGTMevent(ok, labelText, message);
            return { ok, message, status: status ? ERequestStatus.success : ERequestStatus.error };
        } else {
            sendGTMevent(false, labelText);
            return {
                ok: false,
                message: 'Не удалось отправить заявку.',
                status: ERequestStatus.error,
            };
        }
    }, [sendGTMevent]);

    const onStartFormFilling = useCallback((labelText = '') => {
        dataLayerForCBPhonePush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: isAuth ? ACTIONS.startedEquip : ACTIONS.started,
            label: labelText || '',
            uid: isAuth ? agreement : null,
        } as unknown as IDataLayerPushUid);
    }, []);

    return (
        <ST.Content>
            <ST.TextContent>
                <ST.Title>Поможем подобрать роутер</ST.Title>
                <ST.Subtitle>Оставьте свой номер телефона. Менеджер перезвонит вам и поможет подобрать подходящий роутер.</ST.Subtitle>
                <ST.SRequestFormPhone
                    isMobile={isMobile}
                    submitRequest={(params) => submitRequest(params, '')}
                    onStartFormFilling={() => onStartFormFilling('')}
                    btnText='Оставить заявку'
                    templateVariant={'second'}
                />
            </ST.TextContent>
            <ST.ImgContent>
                <WebpImage urlWebp={'/static/images/equipment/callbackimg.webp'}>
                    <ST.Image src={'/static/images/equipment/callbackimg.png'}/>
                </WebpImage>
            </ST.ImgContent>
        </ST.Content>
    );
};

export default CallBackFormRouters;
